import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { Filter, Loader2, Pencil, Plus, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import Swal from 'sweetalert2';
import PageHeader from '@/components/common/PageHeader.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { Q_PROVEEDORES, M_TOGGLE_PROVEEDOR_ESTADO } from '@/graphql/suppliers.gql.js';

const PAGE_SIZE = 8;

const statusOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
];

const condicionIvaLabels = {
  RI: 'Responsable Inscripto',
  MT: 'Monotributo',
  EX: 'Exento',
  CF: 'Consumidor Final',
  RNI: 'Responsable No Inscripto',
};

export default function ListadoProveedores() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 320);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const [result] = useQuery({
    query: Q_PROVEEDORES,
    variables: {
      filter: {
        search: search || undefined,
        estado: status === 'all' ? undefined : status === 'active' ? 'ACTIVO' : 'INACTIVO',
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        orderBy: 'razon_social:ASC',
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const [, toggleActive] = useMutation(M_TOGGLE_PROVEEDOR_ESTADO);

  const suppliers = result.data?.proveedores?.items ?? [];
  const totalItems = result.data?.proveedores?.total ?? 0;
  const totalPages = totalItems ? Math.ceil(totalItems / PAGE_SIZE) : 1;

  const loading = result.fetching;
  const queryError = result.error;

  useEffect(() => {
    if (!totalItems && page !== 1) {
      setPage(1);
      return;
    }
    if (totalItems && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalItems, totalPages]);

  const paginationSummary = useMemo(() => {
    if (!totalItems) {
      return '';
    }
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(totalItems, start + suppliers.length - 1);
    return `Mostrando ${start}-${end} de ${totalItems}`;
  }, [page, suppliers.length, totalItems]);

  const handleToggle = async id => {
    const result = await toggleActive({ id });
    if (result.error) {
      await Swal.fire('No pudimos actualizar el proveedor', result.error.message, 'error');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Proveedores"
        description="Gestioná altas, ediciones y estados de los proveedores vinculados a tus compras."
        breadcrumbs={[
          { label: 'Administración', href: '/admin' },
          { label: 'Proveedores' },
        ]}
        actions={
          <Link
            to="/admin/proveedores/nuevo"
            className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nuevo proveedor
          </Link>
        }
      >
        <span className="rounded-full bg-[rgba(var(--panel),0.55)] px-3 py-1 text-xs font-semibold text-[rgb(var(--brand))]">
          Registro centralizado
        </span>
      </PageHeader>

      <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.75)] p-6 shadow-[0_20px_40px_rgba(var(--shadow),0.14)]">
        <div className="flex flex-col gap-4">
          {queryError ? (
            <div className="rounded-2xl border border-[rgba(var(--danger,255_99_99),0.35)] bg-[rgba(var(--danger,255_99_99),0.08)] px-4 py-3 text-sm text-[rgb(var(--danger,255_99_99))]">
              {queryError.message || 'No se pudieron cargar los proveedores.'}
            </div>
          ) : null}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <label className="sr-only" htmlFor="supplier-search">
                Buscar proveedor
              </label>
              <div className="flex items-center gap-3 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-4 py-2 focus-within:border-[rgb(var(--brand))] focus-within:ring-2 focus-within:ring-[rgb(var(--ring))]">
                <Search className="h-4 w-4 text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
                <input
                  id="supplier-search"
                  type="search"
                  value={searchInput}
                  onChange={event => setSearchInput(event.target.value)}
                  placeholder="Buscar por razón social o CUIT"
                  className="w-full bg-transparent text-sm text-[rgb(var(--fg))] placeholder:text-[rgba(var(--muted),0.75)] focus:outline-none"
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-3">
              <label className="sr-only" htmlFor="supplier-status">
                Estado
              </label>
              <div className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] px-3 py-2">
                <Filter className="h-4 w-4 text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
                <select
                  id="supplier-status"
                  value={status}
                  onChange={event => {
                    setStatus(event.target.value);
                    setPage(1);
                  }}
                  className="w-full bg-transparent text-sm text-[rgb(var(--fg))] focus:outline-none"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value} className="bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-span-12 md:col-span-3 flex items-center justify-end text-sm text-[rgba(var(--muted),0.85)]">
              {paginationSummary}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] shadow-[0_18px_38px_rgba(var(--shadow),0.12)]">
            <table className="min-w-full divide-y divide-[rgba(var(--border),0.5)]">
              <thead className="bg-[rgba(var(--panel),0.9)] text-left text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),0.75)]">
                <tr>
                  <th scope="col" className="px-6 py-3">Razón social</th>
                  <th scope="col" className="px-6 py-3">CUIT</th>
                  <th scope="col" className="px-6 py-3">Condición IVA</th>
                  <th scope="col" className="px-6 py-3">Contacto</th>
                  <th scope="col" className="px-6 py-3">Estado</th>
                  <th scope="col" className="px-6 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(var(--border),0.35)] text-sm text-[rgb(var(--fg))]">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center">
                      <div className="flex items-center justify-center gap-2 text-sm text-[rgba(var(--muted),0.85)]">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        Cargando proveedores...
                      </div>
                    </td>
                  </tr>
                ) : suppliers.length ? (
                  suppliers.map(supplier => (
                    <tr key={supplier.id} className="transition hover:bg-[rgba(var(--panel),0.75)]">
                      <td className="px-6 py-4">
                        <div className="font-semibold">{supplier.razon_social}</div>
                        {supplier.direccion ? (
                          <div className="text-xs text-[rgba(var(--muted),0.8)]">
                            {supplier.direccion}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-[rgba(var(--muted),0.9)]">{supplier.cuit}</td>
                      <td className="px-6 py-4">{condicionIvaLabels[supplier.condicion_iva] || supplier.condicion_iva}</td>
                      <td className="px-6 py-4 text-[rgba(var(--muted),0.85)]">
                        <div className="font-semibold text-[rgb(var(--fg))]">{supplier.contacto_nombre}</div>
                        <div>{supplier.contacto_email}</div>
                        <div>{supplier.contacto_tel}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            supplier.estado === 'ACTIVO'
                              ? 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]'
                              : 'bg-[rgba(var(--muted),0.12)] text-[rgba(var(--muted),0.9)]'
                          }`}
                        >
                          {supplier.estado === 'ACTIVO' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggle(supplier.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] text-[rgb(var(--fg))] shadow-sm transition hover:bg-[rgba(var(--panel),0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            title={supplier.estado === 'ACTIVO' ? 'Desactivar proveedor' : 'Activar proveedor'}
                          >
                            {supplier.estado === 'ACTIVO' ? (
                              <ToggleRight className="h-4 w-4" />
                            ) : (
                              <ToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          <Link
                            to={`/admin/proveedores/${supplier.id}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] text-[rgb(var(--fg))] shadow-sm transition hover:bg-[rgba(var(--panel),0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            title="Editar proveedor"
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Editar {supplier.razon_social}</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-10">
                      <EmptyState
                        title="No encontramos proveedores"
                        description="Probá ajustando los filtros o creá un nuevo proveedor para comenzar."
                        action={
                          <Link
                            to="/admin/proveedores/nuevo"
                            className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(var(--shadow),0.18)] transition hover:bg-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                            Crear proveedor
                          </Link>
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalItems > PAGE_SIZE ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-[rgba(var(--panel),0.55)] p-4 text-sm text-[rgba(var(--muted),0.85)] sm:flex-row sm:items-center sm:justify-between">
              <div>
                Página {page} de {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(page => Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.75)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage(page => Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.75)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Siguiente
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
